import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, Paper, Snackbar, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { PriceChange as BalanceIcon } from '@mui/icons-material';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { adjustClientBalance, parseBalanceAdjustment, summarizeClientBalances } from '../utils/clientBalanceService';

const money = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', signDisplay: 'exceptZero',
}).format(amount);

export default function ClientBalances() {
  const { isSuperAdmin } = useAuth();
  const canAdjust = isSuperAdmin();
  const [data, setData] = useState({});
  const [loadError, setLoadError] = useState('');
  const [retry, setRetry] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedId, setSelectedId] = useState(null);
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const tableRef = useRef(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setData({});
    setLoadError('');
    const unsubs = ['clients', 'client_balance_entries', 'contracts'].map((name) => (
      onSnapshot(collection(db, name), (snapshot) => {
        setData((previous) => ({
          ...previous,
          [name]: snapshot.docs.map((item) => ({ ...item.data(), id: item.id })),
        }));
      }, () => setLoadError('Unable to load client balances. Please retry.'))
    ));
    return () => unsubs.forEach((unsubscribe) => unsubscribe());
  }, [retry]);

  const loading = !data.clients || !data.client_balance_entries || !data.contracts;
  const rows = useMemo(() => summarizeClientBalances(
    data.clients || [], data.client_balance_entries || [], data.contracts || [],
  ), [data]);
  const filtered = useMemo(() => rows.filter((row) => (
    String(row.clientName || '').toLowerCase().includes(search.trim().toLowerCase())
  )), [rows, search]);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1));
  useEffect(() => {
    if (tableRef.current) tableRef.current.scrollTop = 0;
  }, [currentPage, rowsPerPage, search]);
  const selected = rows.find((row) => row.id === selectedId);
  const parsedAmount = parseBalanceAdjustment(amount);
  const history = useMemo(() => (data.client_balance_entries || [])
    .filter((entry) => entry.clientId === selectedId)
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))
      || (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)), [data, selectedId]);
  const totalBalance = rows.reduce((total, row) => total + Math.round(row.balanceUsd * 100), 0) / 100;

  const closeDialog = () => {
    if (!savingRef.current) setSelectedId(null);
  };
  const save = async (event) => {
    event.preventDefault();
    if (savingRef.current || !canAdjust || !selected || parsedAmount === null || loadError || loading) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError('');
    try {
      await adjustClientBalance({ clientId: selected.id, amountUsd: amount, memo });
      setNotice(`${money(parsedAmount)} adjustment saved for ${selected.clientName}.`);
      setSelectedId(null);
    } catch (error) {
      setSaveError(error.message || 'Unable to save the adjustment. Please try again.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ my: 4, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexShrink: 0 }}>
        <BalanceIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight="bold">Client Balances</Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        All client funds in USD, including manual additions and removals. Balance is funds minus amounts applied to invoices.
      </Typography>
      {loadError ? (
        <Alert severity="error" action={<Button color="inherit" onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>
          {loadError}
        </Alert>
      ) : loading ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress aria-label="Loading client balances" /></Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3, flexShrink: 0 }}>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2 }}><Typography color="text.secondary">All clients</Typography><Typography variant="h5">{rows.length}</Typography></Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2 }}><Typography color="text.secondary">Total balance (USD)</Typography><Typography variant="h5">{money(totalBalance)}</Typography></Paper>
            </Grid>
          </Grid>
          <Paper sx={{ flex: '1 0 280px', minHeight: 280, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ p: 2, flexShrink: 0 }}>
              <TextField label="Search clients" size="small" value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }} sx={{ width: { xs: '100%', sm: 320 } }} />
            </Box>
            <TableContainer ref={tableRef} tabIndex={0} role="region" aria-label="Scrollable client balances"
              sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table stickyHeader aria-label="Client balances" sx={{ minWidth: 760 }}>
                <TableHead><TableRow>
                  <TableCell>Client</TableCell><TableCell>Status</TableCell>
                  <TableCell align="right">Funds (USD)</TableCell><TableCell align="right">Applied (USD)</TableCell>
                  <TableCell align="right">Balance (USD)</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {filtered.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage).map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.clientName || 'Unnamed client'}</TableCell>
                      <TableCell><Chip size="small" label={row.status || 'Unknown'} /></TableCell>
                      <TableCell align="right">{money(row.fundsUsd)}</TableCell>
                      <TableCell align="right">{money(row.appliedUsd)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: row.balanceUsd < 0 ? 'error.main' : 'text.primary' }}>{money(row.balanceUsd)}</TableCell>
                      <TableCell align="right"><Button size="small" onClick={() => {
                        setSelectedId(row.id); setAmount(''); setMemo(''); setSaveError('');
                      }}>{canAdjust ? 'Adjust balance' : 'View history'}</Button></TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}>{rows.length ? 'No clients match your search.' : 'No clients yet.'}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={filtered.length} page={currentPage} rowsPerPage={rowsPerPage} sx={{ flexShrink: 0 }}
              onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} />
          </Paper>
        </>
      )}
      <Dialog open={selectedId !== null} onClose={closeDialog} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={save} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <DialogTitle>{canAdjust ? 'Adjust balance' : 'Balance history'} — {selected?.clientName || 'Client'}</DialogTitle>
          <DialogContent>
            {(!selected || loadError) && <Alert severity="error" sx={{ mb: 2 }}>Client balance is unavailable. Close this dialog and reload the page.</Alert>}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Typography>Current balance: <strong>{selected ? money(selected.balanceUsd) : 'Unavailable'}</strong></Typography>
              {canAdjust && <Button variant="outlined" size="small"
                disabled={saving || !selected || selected.balanceUsd === 0 || !!loadError || loading}
                onClick={() => {
                  setAmount((-selected.balanceUsd).toFixed(2));
                  setMemo((previous) => previous || 'Balance reset to zero');
                  setSaveError('');
                }}>Zero balance</Button>}
            </Box>
            {canAdjust && <>
              <TextField autoFocus fullWidth required label="Adjustment (signed USD)" value={amount}
                onChange={(event) => setAmount(event.target.value)} disabled={saving} placeholder="+100.00 or -50.00"
                error={amount !== '' && parsedAmount === null}
                helperText="Use a positive amount to add money or a negative amount to remove money. Up to two decimal places."
                sx={{ mt: 1, mb: 2 }} />
              <TextField fullWidth label="Reason / note (optional)" value={memo} onChange={(event) => setMemo(event.target.value)} disabled={saving} multiline minRows={2} inputProps={{ maxLength: 1000 }} />
              {parsedAmount !== null && selected && <Typography sx={{ mt: 2 }}>
                Balance after adjustment: <strong>{money((Math.round(selected.balanceUsd * 100) + Math.round(parsedAmount * 100)) / 100)}</strong>
              </Typography>}
              <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>This adjustment leaves the ledger unchanged. Negative balances are allowed.</Typography>
              {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
            </>}
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Client fund history</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Deposits, reversals and adjustments. Invoice applications appear in the Applied column.</Typography>
            <TableContainer sx={{ maxHeight: 260 }}>
              <Table size="small" stickyHeader aria-label="Client fund history">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Details</TableCell><TableCell align="right">USD</TableCell></TableRow></TableHead>
                <TableBody>
                  {history.map((entry) => <TableRow key={entry.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.date || '—'}</TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>
                      <Typography variant="body2">{entry.source === 'manual_adjustment' ? 'Manual adjustment' : entry.isReversal ? 'Reversal' : 'Client funds'}</Typography>
                      <Typography variant="caption" color="text.secondary">{entry.memo}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(Number(entry.amountUsd || 0))}</TableCell>
                  </TableRow>)}
                  {!history.length && <TableRow><TableCell colSpan={3}>No fund history yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={saving}>Close</Button>
            {canAdjust && <Button type="submit" variant="contained" disabled={saving || parsedAmount === null || !selected || !!loadError || loading}>{saving ? 'Saving…' : 'Save adjustment'}</Button>}
          </DialogActions>
        </Box>
      </Dialog>
      <Snackbar open={!!notice} autoHideDuration={6000} onClose={() => setNotice('')}>
        <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>
      </Snackbar>
    </Container>
  );
}
